import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Ban, Download, LogIn, LogOut, RefreshCw, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { ENAB } from "./Scan";

const todayCL = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Santiago" });
const nowHM = () => new Date().toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit", hour12: false });
const STATE = { sin_llegar: ["bloqueado", "Sin llegar"], en_faena: ["activo", "En faena"], salio: ["inactivo", "Salió"] };
const KPI = [["asignados", "Asignados", "#17210f"], ["presentes", "En faena", "#258F00"], ["faltan", "Faltan", "#b42318"], ["salieron", "Salieron", "#5b6656"]];

const ManualDialog = ({ target, otId, onClose, onSaved }) => {
  const [f, setF] = useState({ time: nowHM(), reason: "" });
  useEffect(() => { if (target) setF({ time: nowHM(), reason: "" }); }, [target]);
  const save = async () => {
    try {
      const { data } = await api.post(`/rrhh/day/${otId}/marks`, { employee_id: target.row.id, kind: target.kind, ...f });
      data.result.endsWith("_registrada") ? toast.success("Registro guardado") : toast.warning(`No se registró: ${data.result.replace("_", " ")}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manual-mark-dialog">
        <DialogHeader><DialogTitle>Registrar {target?.kind} manual · {target?.row.name}</DialogTitle></DialogHeader>
        <Field label="Hora (hoy) *"><Input data-testid="manual-time-input" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></Field>
        <Field label="Motivo *" hint="Ej.: olvidó el carnet, lector sin señal."><Textarea data-testid="manual-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="manual-save-btn" className="rounded-full" onClick={save}>Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VoidDialog = ({ target, otId, date, onClose, onSaved }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (target) setReason(""); }, [target]);
  const save = async () => {
    try {
      await api.post(`/rrhh/day/${otId}/marks/${target.row.id}/anular`, { kind: target.kind, date, reason });
      toast.success(`${target.kind === "entrada" ? "Entrada" : "Salida"} anulada`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="void-mark-dialog">
        <DialogHeader><DialogTitle>Anular {target?.kind} · {target?.row.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5b6656]">La marca de las {fmtTime(target?.row[target?.kind]?.ts)} se quitará y quedará registrada en auditoría con su motivo.</p>
        <Field label="Motivo *" hint="Ej.: salida marcada por error."><Textarea data-testid="void-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="void-cancel-btn" variant="outline" className="rounded-full" onClick={onClose}>Cancelar</Button><Button data-testid="void-save-btn" variant="destructive" className="rounded-full" disabled={reason.trim().length < 5} onClick={save}>Anular marca</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, canMark, canVoid, onManual, onVoid }) => (
  <div data-testid={`day-row-${r.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] text-sm">
    <EmployeeAvatar id={r.id} v={r.photo_v} name={r.name} size={40} />
    <div className="flex-1 min-w-[160px]"><div className="font-semibold">{r.name}</div><div className="text-xs text-[#5b6656]">{r.rut} · {r.position}{!r.assigned ? " · no asignado" : ""}</div></div>
    <StatusBadge status={ENAB[r.enablement]?.[0]} label={ENAB[r.enablement]?.[1]} />
    <div className="text-xs font-mono w-36" data-testid={`day-times-${r.id}`}>
      <div title={r.entrada?.reason || ""}>E {fmtTime(r.entrada?.ts)}{r.entrada?.via === "manual" ? " (manual)" : r.entrada?.via === "offline" ? " (sin señal)" : ""}</div>
      <div title={r.salida?.reason || ""}>S {fmtTime(r.salida?.ts)}{r.salida?.via === "manual" ? " (manual)" : r.salida?.via === "offline" ? " (sin señal)" : ""}{r.hours != null ? ` · ${r.hours} h` : ""}</div>
    </div>
    <StatusBadge testid={`day-state-${r.id}`} status={STATE[r.state][0]} label={STATE[r.state][1]} />
    {canVoid && (r.salida || r.entrada) && <Button data-testid={`void-mark-${r.id}`} size="sm" variant="ghost" className="rounded-full text-[#b42318]" onClick={() => onVoid({ row: r, kind: r.salida ? "salida" : "entrada" })}><Ban size={13} className="mr-1" />Anular {r.salida ? "salida" : "entrada"}</Button>}
    {canMark && <div className="flex gap-1">
      {r.state === "sin_llegar" && <Button data-testid={`manual-in-${r.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => onManual({ row: r, kind: "entrada" })}><LogIn size={13} className="mr-1" />Entrada</Button>}
      {r.state === "en_faena" && <Button data-testid={`manual-out-${r.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => onManual({ row: r, kind: "salida" })}><LogOut size={13} className="mr-1" />Salida</Button>}
    </div>}
  </div>
);

export default function DayBoard() {
  const { can } = useAuth();
  const [ots, setOts] = useState([]);
  const [params] = useSearchParams();
  const [ot, setOt] = useState(params.get("ot") || "");
  const [date, setDate] = useState(todayCL());
  const [data, setData] = useState(null);
  const [manual, setManual] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/rrhh/control-doc/ots").then((r) => setOts(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => ot && api.get(`/rrhh/day/${ot}`, { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [ot, date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!data?.today) return undefined; const t = setInterval(load, 15000); return () => clearInterval(t); }, [data?.today, load]);
  const rows = (data?.rows || []).filter((r) => !filter || r.state === filter);
  const month = date.slice(0, 7);
  return (
    <div data-testid="day-board-page">
      <PageHeader eyebrow="RR.HH. · Terreno" title="Asistencia del día" subtitle="Quién llegó, quién falta y quién salió en cada OT. Se actualiza solo cada 15 segundos."
        actions={<>
          <Link to="/rrhh/escanear"><Button data-testid="go-scan-btn" className="rounded-full"><ScanLine size={15} className="mr-1" />Escanear</Button></Link>
          {ot && <Button data-testid="day-export-btn" variant="outline" className="rounded-full" onClick={() => download(`/rrhh/day/${ot}/export`, { date_from: `${month}-01`, date_to: `${month}-31` }, `marcas_${month}.xlsx`)}><Download size={15} className="mr-1" />Excel del mes</Button>}
        </>} />
      <div className="syc-card p-4 mb-5 grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
        <Field label="OT"><Pick testid="day-ot-select" value={ot} onChange={setOt} options={ots} placeholder="Seleccione la OT" /></Field>
        <Field label="Fecha"><Input data-testid="day-date-input" type="date" value={date} max={todayCL()} onChange={(e) => setDate(e.target.value || todayCL())} /></Field>
        <Button data-testid="day-refresh-btn" variant="outline" className="rounded-full" onClick={load} disabled={!ot}><RefreshCw size={15} className="mr-1" />Actualizar</Button>
      </div>
      {!data ? <div className="syc-card"><EmptyState title="Seleccione una OT" /></div> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">{KPI.map(([k, l, c]) => (
          <button key={k} data-testid={`day-kpi-${k}`} onClick={() => setFilter(k === "asignados" ? "" : { presentes: "en_faena", faltan: "sin_llegar", salieron: "salio" }[k])}
            className={`syc-card p-4 text-left transition-transform hover:-translate-y-0.5 ${filter === ({ presentes: "en_faena", faltan: "sin_llegar", salieron: "salio" }[k] || "") ? "ring-2 ring-[#5CD500]" : ""}`}>
            <div className="text-xs uppercase tracking-wider text-[#5b6656]">{l}</div><div className="text-3xl font-extrabold" style={{ color: c }}>{data.kpis[k]}</div>
          </button>))}</div>
        <div className="syc-card overflow-hidden">
          {data.today && <div className="flex items-center gap-2 px-5 py-2 text-xs text-[#258F00] border-b border-[#e4e8e1]"><span className="h-2 w-2 rounded-full bg-[#5CD500] animate-pulse" />En vivo · {data.ot.code}</div>}
          {rows.length === 0 ? <EmptyState title="Sin trabajadores" text="Asigne trabajadores a la OT en Control Documental OT." /> : rows.map((r) => <Row key={r.id} r={r} canMark={data.today && can("rrhh.asistencia.registrar")} canVoid={can("rrhh.asistencia.anular")} onManual={setManual} onVoid={setVoiding} />)}
        </div>
      </>}
      <ManualDialog target={manual} otId={ot} onClose={() => setManual(null)} onSaved={() => { setManual(null); load(); }} />
      <VoidDialog target={voiding} otId={ot} date={data?.date} onClose={() => setVoiding(null)} onSaved={() => { setVoiding(null); load(); }} />
    </div>
  );
}
